/**
 * GeoPerformanceTab — Geographic Performance for TJ Command Center Phase 1.5C
 *
 * Shows ad performance broken down by US state with sortable table,
 * top/bottom state cards, and campaign filter.
 */

import { useState, useMemo } from "react";
import {
  useAdPerformanceGeo,
  useAdCampaigns,
  type AdPerformanceGeo,
} from "@/hooks/useAdPerformance";
import {
  overlaySystemLeadsOnGeo,
  type SystemLeadsOverlay,
} from "@/hooks/useSystemLeads";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, MapPin, TrendingDown, TrendingUp, ArrowUpDown } from "lucide-react";

// ─── Types ──────────────────────────────────────────────

interface StateRow {
  stateCode: string;
  region: string;
  spend: number;
  impressions: number;
  reach: number;
  clicks: number;
  linkClicks: number;
  leads: number;
  cpl: number;
  ctr: number;
  cpc: number;
  cpm: number;
}

type SortKey = "spend" | "leads" | "cpl" | "ctr" | "cpc" | "impressions" | "clicks";

// ─── Helpers ────────────────────────────────────────────

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("en-US").format(Math.round(value));
}

function formatPct(value: number): string {
  return `${value.toFixed(2)}%`;
}

function aggregateGeoData(geoSnaps: AdPerformanceGeo[]): StateRow[] {
  const stateMap = new Map<
    string,
    { region: string; spend: number; impressions: number; reach: number; clicks: number; linkClicks: number; leads: number }
  >();

  for (const snap of geoSnaps) {
    const key = snap.state_code || snap.region;
    const existing = stateMap.get(key);
    if (existing) {
      existing.spend += snap.spend || 0;
      existing.impressions += snap.impressions || 0;
      existing.reach += snap.reach || 0;
      existing.clicks += snap.clicks || 0;
      existing.linkClicks += snap.link_clicks || 0;
      existing.leads += snap.leads_count || 0;
    } else {
      stateMap.set(key, {
        region: snap.region,
        spend: snap.spend || 0,
        impressions: snap.impressions || 0,
        reach: snap.reach || 0,
        clicks: snap.clicks || 0,
        linkClicks: snap.link_clicks || 0,
        leads: snap.leads_count || 0,
      });
    }
  }

  const rows: StateRow[] = [];
  for (const [stateCode, data] of stateMap) {
    rows.push({
      stateCode,
      region: data.region,
      spend: data.spend,
      impressions: data.impressions,
      reach: data.reach,
      clicks: data.clicks,
      linkClicks: data.linkClicks,
      leads: data.leads,
      cpl: data.leads > 0 ? data.spend / data.leads : 0,
      ctr: data.impressions > 0 ? (data.clicks / data.impressions) * 100 : 0,
      cpc: data.clicks > 0 ? data.spend / data.clicks : 0,
      cpm: data.impressions > 0 ? (data.spend / data.impressions) * 1000 : 0,
    });
  }

  return rows;
}

// CPL color coding: green for low, red for high (relative to average)
function getCplColor(cpl: number, avgCpl: number): string {
  if (cpl <= 0) return "";
  if (avgCpl <= 0) return "";
  const ratio = cpl / avgCpl;
  if (ratio <= 0.75) return "text-green-700 font-bold";
  if (ratio <= 0.95) return "text-green-600";
  if (ratio >= 1.5) return "text-red-700 font-bold";
  if (ratio >= 1.15) return "text-red-600";
  return "";
}

// ─── Main Component ─────────────────────────────────────

export function GeoPerformanceTab({
  dateFrom,
  dateTo,
  systemLeadsOverlay,
}: {
  dateFrom: string;
  dateTo: string;
  systemLeadsOverlay?: SystemLeadsOverlay;
}) {
  const [campaignFilter, setCampaignFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<SortKey>("spend");
  const [sortAsc, setSortAsc] = useState(false);

  const campaigns = useAdCampaigns();
  const { data: rawGeoSnaps = [], isLoading } = useAdPerformanceGeo({
    dateFrom,
    dateTo,
    campaignId: campaignFilter === "all" ? undefined : campaignFilter,
  });

  // Overlay system leads onto geo data
  const geoSnaps = useMemo(
    () => systemLeadsOverlay ? overlaySystemLeadsOnGeo(rawGeoSnaps, systemLeadsOverlay) : rawGeoSnaps,
    [rawGeoSnaps, systemLeadsOverlay]
  );

  const stateRows = useMemo(() => {
    const rows = aggregateGeoData(geoSnaps);

    // Sort
    rows.sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      // CPL: lower is better, so default desc should show highest first for consistency
      return sortAsc ? aVal - bVal : bVal - aVal;
    });

    return rows;
  }, [geoSnaps, sortBy, sortAsc]);

  const avgCpl = useMemo(() => {
    const totalSpend = stateRows.reduce((s, r) => s + r.spend, 0);
    const totalLeads = stateRows.reduce((s, r) => s + r.leads, 0);
    return totalLeads > 0 ? totalSpend / totalLeads : 0;
  }, [stateRows]);

  const totalSpend = stateRows.reduce((s, r) => s + r.spend, 0);
  const totalLeads = stateRows.reduce((s, r) => s + r.leads, 0);

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortBy(key);
      setSortAsc(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (stateRows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <MapPin className="h-12 w-12 mb-4" />
        <p className="text-lg font-medium">No geographic data yet</p>
        <p className="text-sm mt-1">
          Geographic breakdown syncs automatically with your Meta Ads data.
        </p>
      </div>
    );
  }

  // Top 3 and bottom 3 states by CPL (only states with leads)
  const statesWithLeads = stateRows.filter((r) => r.leads > 0);
  const topStates = [...statesWithLeads].sort((a, b) => a.cpl - b.cpl).slice(0, 3);
  const bottomStates = [...statesWithLeads].sort((a, b) => b.cpl - a.cpl).slice(0, 3);

  const SortHeader = ({ label, sortKey, className }: { label: string; sortKey: SortKey; className?: string }) => (
    <TableHead className={className}>
      <button
        className="flex items-center gap-1 hover:text-foreground transition-colors"
        onClick={() => handleSort(sortKey)}
      >
        {label}
        {sortBy === sortKey && (
          <ArrowUpDown className="h-3 w-3" />
        )}
      </button>
    </TableHead>
  );

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">States Active</p>
            <p className="text-xl font-bold">{stateRows.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Total Spend</p>
            <p className="text-xl font-bold">{formatCurrency(totalSpend)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Total Leads</p>
            <p className="text-xl font-bold">{formatNumber(totalLeads)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Avg CPL</p>
            <p className="text-xl font-bold">{avgCpl > 0 ? formatCurrency(avgCpl) : "—"}</p>
          </CardContent>
        </Card>
      </div>

      {/* Top/Bottom States */}
      {statesWithLeads.length >= 2 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                <TrendingDown className="h-4 w-4 text-green-600" />
                Best CPL States
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {topStates.map((s, i) => (
                <div key={s.stateCode} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="w-8 justify-center text-xs font-mono">
                      {i + 1}
                    </Badge>
                    <span className="font-medium">{s.stateCode}</span>
                    <span className="text-muted-foreground text-xs">{s.region}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-green-700 font-semibold font-mono">{formatCurrency(s.cpl)}</span>
                    <span className="text-xs text-muted-foreground">{s.leads} leads</span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                <TrendingUp className="h-4 w-4 text-red-600" />
                Highest CPL States
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {bottomStates.map((s, i) => (
                <div key={s.stateCode} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="w-8 justify-center text-xs font-mono">
                      {i + 1}
                    </Badge>
                    <span className="font-medium">{s.stateCode}</span>
                    <span className="text-muted-foreground text-xs">{s.region}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-red-700 font-semibold font-mono">{formatCurrency(s.cpl)}</span>
                    <span className="text-xs text-muted-foreground">{s.leads} leads</span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Campaign Filter */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Campaign:</span>
        <Select value={campaignFilter} onValueChange={setCampaignFilter}>
          <SelectTrigger className="w-[250px]">
            <SelectValue placeholder="All campaigns" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Campaigns</SelectItem>
            {(campaigns.data ?? []).map((c) => (
              <SelectItem key={c.meta_campaign_id} value={c.meta_campaign_id}>
                {c.meta_campaign_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* State Table */}
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[80px]">State</TableHead>
              <TableHead>Region</TableHead>
              <SortHeader label="Spend" sortKey="spend" className="text-right" />
              <SortHeader label="Leads" sortKey="leads" className="text-right" />
              <SortHeader label="CPL" sortKey="cpl" className="text-right" />
              <SortHeader label="CTR" sortKey="ctr" className="text-right" />
              <SortHeader label="CPC" sortKey="cpc" className="text-right" />
              <SortHeader label="Impr." sortKey="impressions" className="text-right" />
              <SortHeader label="Clicks" sortKey="clicks" className="text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {stateRows.map((row) => (
              <TableRow key={row.stateCode}>
                <TableCell>
                  <span className="font-mono font-bold text-sm">{row.stateCode}</span>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{row.region}</TableCell>
                <TableCell className="text-right font-mono text-sm">{formatCurrency(row.spend)}</TableCell>
                <TableCell className="text-right text-sm">{formatNumber(row.leads)}</TableCell>
                <TableCell className={`text-right font-mono text-sm ${getCplColor(row.cpl, avgCpl)}`}>
                  {row.leads > 0 ? formatCurrency(row.cpl) : "—"}
                </TableCell>
                <TableCell className="text-right text-sm">{formatPct(row.ctr)}</TableCell>
                <TableCell className="text-right font-mono text-sm">
                  {row.clicks > 0 ? formatCurrency(row.cpc) : "—"}
                </TableCell>
                <TableCell className="text-right text-sm">{formatNumber(row.impressions)}</TableCell>
                <TableCell className="text-right text-sm">{formatNumber(row.clicks)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
